import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Card } from '@material-ui/core';
import Checkbox from '@material-ui/core/Checkbox';
import TextField from '@material-ui/core/TextField';
import { createStyles, makeStyles } from '@material-ui/core/styles';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import SearchIcon from '@material-ui/icons/Search';
import Autocomplete from '@material-ui/lab/Autocomplete';
import clsx from 'clsx';
import { forwardRef, useMemo, useState } from 'react';
const useStyles = makeStyles((theme) => createStyles({
    root: {
        '& .MuiInputLabel-outlined.MuiInputLabel-shrink': {
            backgroundColor: theme.palette.background.default,
        },
    },
    autocompleteOptionsPaper: {
        borderTopLeftRadius: 0,
        borderTopRightRadius: 0,
        padding: theme.spacing(1),
        '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: theme.palette.statesPrimary?.selected,
        },
    },
    inputRootActive: {
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
            borderColor: theme.palette.primary.main,
        },
    },
    searchRoot: {
        display: 'flex',
        alignItems: 'center',
        borderBottom: `1px solid ${theme.palette.divider}`,
        '& .MuiInputBase-root': {
            padding: theme.spacing(1),
        },
        '& .MuiInputBase-input': {
            padding: theme.spacing(0, 1),
            pointerEvents: 'none',
        },
    },
    searchText: {
        padding: theme.spacing(1),
    },
    optionCheckbox: {
        '&:hover': {
            background: 'transparent',
        },
    },
}));
/**
 * AutocompleteMultiSelect component
 *
 * @param {boolean} props.disabled - Optional flag to disable the input.
 * @param {K} props.keyField - Optional key field name. Defaults to `key`.
 * @param {string} props.label - The label for the main read-only input field.
 * @param {L} props.labelField - Optional label field name. Defaults to `label`.
 * @param {MultiSelectOption<K, L>[]} props.options - The options to be displayed in the dropdown.
 * @param {boolean} [props.required] - Whether the input is required.
 * @param {string} [props.testId] - The test id for the checkbox.
 * @param {MultiSelectOption<K, L>[]} [props.value] - The currently selected options.
 * @param {(value: MultiSelectOption<K, L>[]) => void} props.onChange - The function to call when the selected options change.
 * @returns {JSX.Element} The rendered AutocompleteMultiSelect component.
 */
export const AutocompleteMultiSelect = forwardRef(({ disabled, error = false, errorText, options, label, onChange, value = [], testId, required, keyField = 'key', labelField = 'label', ...props }, ref) => {
    const classes = useStyles();
    const [searchValue, setSearchValue] = useState('');
    const [popupOpen, setPopupOpen] = useState(false);
    const isAllSelected = value.length === options.length;
    const allOptions = useMemo(() => {
        return [{ [labelField]: 'Select All', [keyField]: 'selectAll' }, ...options];
    }, [options, keyField, labelField]);
    const filterOptions = (opts) => {
        return opts.filter((option) => option[keyField] === 'selectAll' || option[labelField].toLowerCase().includes(searchValue.toLowerCase()));
    };
    const handleChange = (_, newValue, __, details) => {
        if (details?.option?.[keyField] === 'selectAll') {
            onChange(newValue.some((option) => option[keyField] === 'selectAll') && !isAllSelected ? options : []);
        }
        else {
            onChange(newValue.filter((option) => option[keyField] !== 'selectAll'));
        }
    };
    const renderInput = (params) => (_jsx(TextField, { ...params, className: clsx(classes.root, { [classes.inputRootActive]: popupOpen }), error: error, helperText: errorText, variant: 'outlined', label: label, margin: 'dense', required: required, value: !popupOpen ? `${value.length} Selected` : '', InputProps: {
            ...params.InputProps,
            readOnly: true,
        }, onFocus: () => setPopupOpen(true) }));
    const renderOption = (option) => {
        const isSelectAll = option[keyField] === 'selectAll';
        const checked = isSelectAll
            ? isAllSelected
            : value.some((selected) => selected[keyField] === option[keyField]);
        return (_jsxs(_Fragment, { children: [_jsx(Checkbox, { icon: _jsx(CheckBoxOutlineBlankIcon, { fontSize: 'small', color: 'secondary' }), checkedIcon: _jsx(CheckBoxIcon, { fontSize: 'small', color: 'primary' }), checked: checked, disableFocusRipple: true, disableRipple: true, disableTouchRipple: true, "data-testid": testId, className: classes.optionCheckbox }), option[labelField]] }));
    };
    const renderTags = (selected) => {
        const isAllSelected = selected.length === options.length && options.length > 0;
        const firstSelected = selected.length === 1;
        return (_jsx("span", { className: classes.searchText, children: isAllSelected ? 'All Selected' : firstSelected ? selected[0][labelField] : selected.length + ' Selected' }));
    };
    return (_jsx(Autocomplete, { "aria-required": required, "data-testid": testId, disableClearable: true, disableCloseOnSelect: true, disabled: disabled, disablePortal: true, filterOptions: filterOptions, getLimitTagsText: (selectedCount) => `${selectedCount} Selected`, getOptionLabel: (option) => option[labelField], getOptionSelected: (option, selectedOption) => {
            return option[keyField] === selectedOption?.[keyField];
        }, inputValue: searchValue, multiple: true, open: popupOpen, options: allOptions, value: value, onChange: handleChange, onClose: () => setPopupOpen(false), onInputChange: (_, newInputValue) => setSearchValue(newInputValue), onOpen: () => setPopupOpen(true), ...props, renderInput: renderInput, renderOption: renderOption, renderTags: renderTags, ref: ref, PaperComponent: (paperProps) => (_jsxs(Card, { className: classes.autocompleteOptionsPaper, ...paperProps, children: [_jsx(TextField, { className: classes.searchRoot, value: searchValue, onChange: (e) => setSearchValue(e.target.value), placeholder: 'Search...', variant: 'standard', fullWidth: true, autoFocus: true, onMouseDown: (e) => e.preventDefault(), onFocus: () => setPopupOpen(true), InputProps: {
                        startAdornment: _jsx(SearchIcon, { color: 'secondary', fontSize: 'small' }),
                    }, onBlur: () => setPopupOpen(false) }), paperProps.children] })) }));
});
